/**
 * The iuvo Guardian collector app's grants (appId 10ab017c-a1da-422f-be0e-fda46c03bec9), as the
 * public permissions guide renders them. Kept out of PermissionsGuide.tsx so that file only
 * exports components.
 *
 * Source of truth: Policies/common/guardian_app_permissions.json. DocsPages.test.tsx fails if
 * the Graph lists below drift from its required Microsoft Graph roles. These lists describe the
 * trimmed app; deploy them with the app-registration change, not before.
 */

export interface Permission {
  permission: string
  purpose: string
}

export const identityPermissions: Permission[] = [
  { permission: 'Organization.Read.All', purpose: 'Read organization details and license subscriptions for user analytics and the drift-monitoring license check. Teams PowerShell also requires it to sign in as an application.' },
  { permission: 'Directory.Read.All', purpose: 'Read directory settings and delegated permission grants for Best Practice checks and application insights' },
  { permission: 'User.Read.All', purpose: 'Read user profiles and sign-in activity for user analytics and application insights' },
  { permission: 'Group.Read.All', purpose: 'Read the members of groups that hold admin roles, for user analytics. The SharePoint hub site export also requires it.' },
  { permission: 'Domain.Read.All', purpose: 'Read your domains for password policy checks and to find the tenant’s initial domain. The SharePoint and OneDrive configuration export also requires it.' },
  { permission: 'Application.Read.All', purpose: 'Read app registrations, enterprise apps, their owners and permission grants for application insights, and confirm Guardian’s own consent' },
  { permission: 'UserAuthenticationMethod.Read.All', purpose: 'Read the sign-in methods your admins have registered, for the Best Practice admin MFA check' },
]

export const securityPermissions: Permission[] = [
  { permission: 'Policy.Read.All', purpose: 'Read Conditional Access, named location, authentication method and strength, authorization, admin consent, cross-tenant access and security defaults policies' },
  { permission: 'AuditLog.Read.All', purpose: 'Read the MFA registration report and sign-in activity for user analytics, the directory audit events that name who made a detected change, and app sign-in activity for application insights' },
  { permission: 'SecurityEvents.Read.All', purpose: 'Read Microsoft Secure Score and its improvement actions' },
  { permission: 'AttackSimulation.Read.All', purpose: 'Read Attack Simulation Training runs for the Best Practice phishing simulation check' },
  { permission: 'RoleManagement.Read.Directory', purpose: 'Read directory role assignments, PIM schedules and role policies for privileged access checks and application insights' },
]

export const intunePermissions: Permission[] = [
  { permission: 'DeviceManagementConfiguration.Read.All', purpose: 'Read compliance policies, device configurations, endpoint security and Settings Catalog policies for Best Practice checks' },
  { permission: 'DeviceManagementApps.Read.All', purpose: 'Read app protection policies for Best Practice checks' },
  { permission: 'DeviceManagementServiceConfig.Read.All', purpose: 'Read enrollment restrictions for Best Practice checks' },
]

export const sharePointSettingsPermissions: Permission[] = [
  { permission: 'SharePointTenantSettings.Read.All', purpose: 'Read SharePoint tenant settings for Best Practice checks and SharePoint drift monitoring' },
]

export const monitoringPermissions: Permission[] = [
  { permission: 'ConfigurationMonitoring.ReadWrite.All', purpose: 'Write-capable. Create and maintain Guardian’s own monitors and configuration snapshots in the Microsoft 365 Tenant Configuration Manager, and read the drift they report. It does not change your tenant settings.' },
]

/** Every Microsoft Graph permission the iuvo Guardian app requests, as the guide renders them. */
export const GUARDIAN_GRAPH_PERMISSIONS: readonly Permission[] = [
  ...identityPermissions,
  ...securityPermissions,
  ...intunePermissions,
  ...sharePointSettingsPermissions,
  ...monitoringPermissions,
]

export const guardianSharePointPermissions: Permission[] = [
  { permission: 'Sites.FullControl.All', purpose: 'Write-capable. Read SharePoint and OneDrive tenant configuration through the SharePoint admin APIs used by drift monitoring, Best Practice checks and ScubaGear. Microsoft offers no narrower application permission for those APIs, and Guardian does not use the write side.' },
]

export const guardianExchangePermissions: Permission[] = [
  { permission: 'Exchange.ManageAsApp', purpose: 'Sign in to Exchange Online and Security & Compliance PowerShell as an application. What a session can do is set by the roles below, which are read-only.' },
  { permission: 'Global Reader (directory role)', purpose: 'Read access to Exchange and other workload configuration in those sessions' },
  { permission: 'Security Reader (directory role)', purpose: 'Read access to security and compliance settings, such as data loss prevention policies' },
  { permission: 'View-Only Recipients (Exchange role)', purpose: 'Read-only access to recipient objects. Guardian uses it to tell shared mailboxes from user mailboxes for accurate user analytics' },
]

/** guardian_app_permissions.json optional.scubaPimForGroups: on the app only when iuvo turns ScubaGear's PIM for Groups checks on. */
export const scubaPimPermissions: Permission[] = [
  { permission: 'PrivilegedAccess.Read.AzureADGroup', purpose: 'Read PIM for Groups assignments' },
  { permission: 'PrivilegedEligibilitySchedule.Read.AzureADGroup', purpose: 'Read PIM for Groups eligibility schedules' },
  { permission: 'RoleManagementPolicy.Read.AzureADGroup', purpose: 'Read PIM for Groups role settings' },
]
